import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useToast } from '../../hooks/useToast';
import { useTenantContext } from '../../context/TenantContext';
import { Smartphone, Send, Check, RefreshCw, AlertCircle, Sparkles, Crown, Diamond, Gem, Wrench, Server, Code, HardDrive, Shield, Award, Zap, Cloud, BookOpen, Star } from 'lucide-react';
import deviceSyncService from '../../services/deviceSyncService';

// Mapping d'icônes pour personnalisation
const iconMap: Record<string, React.ElementType> = {
  BookOpen,
  Sparkles,
  Star,
  Diamond,
  Gem,
  Crown,
  Wrench,
  Server,
  Code,
  HardDrive,
  Shield,
  Award,
  Zap,
  Cloud,
};

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface DeviceSyncComponentProps {
  email: string;
}

const DeviceSyncComponent: React.FC<DeviceSyncComponentProps> = ({ email }) => {
  const { toast } = useToast();
  const { tenant, previewConfig } = useTenantContext();

  // Thème Shop (preview > tenant)
  const shopTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b',
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'BookOpen', color: themeColors.c1, size: 32 },
        { enabled: true, icon: 'Sparkles', color: themeColors.c2, size: 28 },
        { enabled: true, icon: 'Diamond', color: themeColors.c2, size: 24 },
        { enabled: true, icon: 'Star', color: themeColors.c1, size: 30 },
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  const [activeTab, setActiveTab] = useState<string>('sync');
  const [isSendingOTP, setIsSendingOTP] = useState<boolean>(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState<boolean>(false);
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [otpCode, setOtpCode] = useState<string>('');
  
  const handleSendOTP = async () => {
    if (!email) {
      toast({
        title: 'Erreur',
        description: 'Email non disponible',
        variant: 'destructive'
      });
      return;
    }
    
    setIsSendingOTP(true);
    
    try {
      const result = await deviceSyncService.sendSyncOTP(email);
      
      if (result.success) {
        setOtpSent(true);
        toast({
          title: 'Code envoyé',
          description: 'Un code de vérification a été envoyé à votre adresse email',
          variant: 'default'
        });
      } else {
        toast({
          title: 'Erreur',
          description: result.error || 'Impossible d\'envoyer le code de vérification',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du code OTP:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'envoi du code',
        variant: 'destructive'
      });
    } finally {
      setIsSendingOTP(false);
    }
  };
  
  const handleVerifyOTP = async () => {
    if (!otpCode) {
      toast({
        title: 'Erreur',
        description: 'Veuillez saisir le code de vérification',
        variant: 'destructive'
      });
      return;
    }
    
    setIsVerifyingOTP(true);
    
    try {
      const result = await deviceSyncService.verifySyncOTP(email, otpCode);
      
      if (result.success) {
        toast({
          title: 'Synchronisation réussie',
          description: 'Vos données ont été synchronisées avec succès',
          variant: 'default'
        });
        
        // Réinitialiser le formulaire
        setOtpSent(false);
        setOtpCode('');
        
        // Recharger la page pour afficher les données synchronisées
        window.location.reload();
      } else {
        toast({
          title: 'Erreur',
          description: result.error || 'Code de vérification incorrect',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du code OTP:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la vérification du code',
        variant: 'destructive'
      });
    } finally {
      setIsVerifyingOTP(false);
    }
  };
  
  const handleSyncCart = async () => {
    try {
      const result = await deviceSyncService.syncCart();
      
      if (result.success) {
        toast({
          title: 'Panier synchronisé',
          description: 'Votre panier a été synchronisé avec succès',
          variant: 'default'
        });
      } else {
        toast({
          title: 'Erreur',
          description: result.error || 'Impossible de synchroniser votre panier',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la synchronisation du panier:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la synchronisation',
        variant: 'destructive'
      });
    }
  };
  
  return (
    <Card className="bg-white/5 border backdrop-blur-sm text-white"
      style={{
        borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
      }}
    >
      <CardHeader>
        <CardTitle className="flex items-center" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
          <Smartphone className="h-5 w-5 mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
          Synchronisation entre appareils
        </CardTitle>
        <CardDescription style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>
          Synchronisez votre panier et vos préférences entre différents appareils
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <Tabs defaultValue="sync" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 bg-white/10 border"
            style={{
              borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)
            }}
          >
            <TabsTrigger value="sync" className="data-[state=active]:bg-white/20 transition-all duration-200"
              style={{
                color: tenant?.config?.color3 || '#ffffff'
              }}
            >
              Synchroniser maintenant
            </TabsTrigger>
            <TabsTrigger value="other-device" className="data-[state=active]:bg-white/20 transition-all duration-200"
              style={{
                color: tenant?.config?.color3 || '#ffffff'
              }}
            >
              Autre appareil
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="sync" className="space-y-4 mt-4">
            <div className="space-y-2">
              <p style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}>
                Synchronisez votre panier et vos préférences avec le serveur pour les retrouver sur tous vos appareils.
              </p>
              
              <div className="border rounded-lg p-4"
                style={{
                  backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.05),
                  borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)
                }}
              >
                <p className="text-sm mb-4" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>
                  Cette action enregistrera votre panier actuel et vos préférences sur le serveur.
                  Vous pourrez les récupérer sur d'autres appareils en utilisant votre email et un code de vérification.
                </p>
                
                <Button 
                  onClick={handleSyncCart}
                  className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                  style={{
                    backgroundColor: tenant?.config?.color1 || '#fe8d17',
                    color: tenant?.config?.color3 || '#ffffff',
                    backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                    borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Synchroniser maintenant
                </Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="other-device" className="space-y-4 mt-4">
            {!otpSent ? (
              <div className="space-y-2">
                <p style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}>
                  Pour récupérer vos données depuis un autre appareil, nous vous enverrons un code de vérification par email.
                </p>
                
                <div className="border rounded-lg p-4"
                  style={{
                    backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.05),
                    borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)
                  }}
                >
                  <p className="text-sm mb-4" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>
                    Un code à 6 chiffres sera envoyé à votre adresse email ({email}).
                    Ce code est valable pendant 10 minutes.
                  </p>
                  
                  <Button 
                    onClick={handleSendOTP}
                    disabled={isSendingOTP}
                    className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                    style={{
                      backgroundColor: tenant?.config?.color2 || '#008aef',
                      color: tenant?.config?.color3 || '#ffffff',
                      backgroundImage: `linear-gradient(to right, ${tenant?.config?.color2 || '#008aef'}, ${tenant?.config?.color2 || '#0074cc'})`,
                      borderColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.4)
                    }}
                    onMouseEnter={(e) => {
                      if (!isSendingOTP) {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color2 || '#0074cc'}, ${tenant?.config?.color2 || '#008aef'})`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSendingOTP) {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color2 || '#008aef'}, ${tenant?.config?.color2 || '#0074cc'})`;
                      }
                    }}
                  >
                    {isSendingOTP ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 mr-2" style={{ borderColor: tenant?.config?.color3 || '#ffffff' }}></div>
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Envoyer le code de vérification
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}>
                  Veuillez saisir le code de vérification envoyé à votre adresse email.
                </p>
                
                <div className="space-y-3">
                  <Label htmlFor="otp" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Code de vérification</Label>
                  <Input
                    id="otp"
                    placeholder="Entrez le code à 6 chiffres"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    maxLength={6}
                    className="text-center text-lg tracking-widest bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                    style={{
                      color: tenant?.config?.color3 || '#ffffff',
                      '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                    } as React.CSSProperties}
                  />
                </div>
                
                <div className="flex space-x-2">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setOtpSent(false);
                      setOtpCode('');
                    }}
                    className="flex-1 transition-all duration-200"
                    style={{
                      borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.3),
                      color: tenant?.config?.color3 || '#ffffff'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.1);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Annuler
                  </Button>
                  
                  <Button 
                    onClick={handleVerifyOTP}
                    disabled={isVerifyingOTP || otpCode.length !== 6}
                    className="flex-1 shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                    style={{
                      backgroundColor: tenant?.config?.color1 || '#fe8d17',
                      color: tenant?.config?.color3 || '#ffffff',
                      backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                      borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)
                    }}
                    onMouseEnter={(e) => {
                      if (!isVerifyingOTP && otpCode.length === 6) {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isVerifyingOTP && otpCode.length === 6) {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                      }
                    }}
                  >
                    {isVerifyingOTP ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 mr-2" style={{ borderColor: tenant?.config?.color3 || '#ffffff' }}></div>
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Vérifier
                  </Button>
                </div>
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleSendOTP}
                    disabled={isSendingOTP}
                    className="text-sm underline transition-colors"
                    style={{
                      color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7)
                    }}
                    onMouseEnter={(e) => {
                      if (!isSendingOTP) {
                        e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSendingOTP) {
                        e.currentTarget.style.color = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7);
                      }
                    }}
                  >
                    {isSendingOTP ? 'Envoi en cours...' : 'Renvoyer le code'}
                  </button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      
      <CardFooter className="flex justify-center border-t pt-4" style={{ borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1) }}>
        <div className="flex items-center text-sm" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>
          <AlertCircle className="h-4 w-4 mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
          Vos données sont synchronisées uniquement entre vos appareils
        </div>
      </CardFooter>
    </Card>
  );
};

export default DeviceSyncComponent;
